// src/utils/coverMessage.js
// 3.67.0 (owner, Oct 23): the cover sheet message in Compose is formatted
// text (bold, italic, underline, lists), still at most 3,000 characters. The backend
// (lib/richText.js) reads the same HTML and prints what doesn't fit on the
// cover sheet on "Message (continued)" pages.

export const MAX_CHARS = 3000;

const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
// A plain message (the client's Cover Sheet setting) → editor HTML
export const fromPlain = text => (!String(text || '').trim() ? ''
  : String(text).replace(/\r/g, '').split('\n').map(l => `<div>${l ? esc(l) : '<br>'}</div>`).join(''));

// Editor HTML → plain text, one line per line
export function toText(html) {
  const doc = new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html');
  const out = [];
  let line = '';
  const end = () => { out.push(line); line = ''; };
  const walk = (node, list) => {
    for (const n of node.childNodes) {
      if (n.nodeType === 3) { line += n.nodeValue.replace(/\s+/g, ' '); continue; }
      if (n.nodeType !== 1) continue;
      const tag = n.tagName.toLowerCase();
      if (tag === 'br') { end(); continue; }
      if (tag === 'ul' || tag === 'ol') { if (line.trim()) end(); walk(n, { kind: tag, n: 0 }); continue; }
      if (tag === 'li') {
        if (line.trim()) end();
        line = list?.kind === 'ol' ? `${++list.n}. ` : '• ';
        walk(n, list); if (line.trim()) end(); continue;
      }
      const block = /^(div|p|h[1-6]|blockquote|tr)$/.test(tag);
      if (block && line.trim()) end();
      walk(n, list);
      if (block && line.trim()) end();
    }
  };
  walk(doc.body, null);
  if (line.trim()) end();
  return out.map(l => l.trim() ? l.replace(/^ +/, '') : '').join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

// Characters, counted like the old box: the text and its line breaks (list
// bullets and numbers aren't typed, so they don't count)
export const countChars = html => toText(html).replace(/^(•|\d+\.) /gm, '').length;

// Pasted HTML (Word, a web page, an email) → only what a fax can print:
// bold, italic, underline, lists and lines; colours, sizes, fonts, links,
// pictures and tables are dropped (their text is kept)
const KEEP = { b: 'b', strong: 'b', i: 'i', em: 'i', u: 'u', ul: 'ul', ol: 'ol', li: 'li', br: 'br', p: 'div', div: 'div' };
export function cleanPaste(html) {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  const walk = node => {
    let s = '';
    for (const n of node.childNodes) {
      if (n.nodeType === 3) { s += esc(n.nodeValue.replace(/\s+/g, ' ')); continue; }
      if (n.nodeType !== 1) continue;
      const tag = n.tagName.toLowerCase();
      if (/^(style|script|head|title|meta|img|svg)$/.test(tag)) continue;
      const inner = walk(n);
      // Word marks bold/italic with styles, not tags
      const st = n.getAttribute('style') || '';
      let text = inner;
      if (/font-weight\s*:\s*(bold|[6-9]00)/i.test(st) && tag !== 'b' && tag !== 'strong') text = `<b>${text}</b>`;
      if (/font-style\s*:\s*italic/i.test(st) && tag !== 'i' && tag !== 'em') text = `<i>${text}</i>`;
      if (/text-decoration[^;]*underline/i.test(st) && tag !== 'u') text = `<u>${text}</u>`;
      const k = KEEP[tag] || (/^(h[1-6]|tr|blockquote)$/.test(tag) ? 'div' : null);
      s += !k ? text + (tag === 'td' || tag === 'th' ? ' ' : '') : k === 'br' ? '<br>' : `<${k}>${text}</${k}>`;
    }
    return s;
  };
  return walk(doc.body);
}

// About how many pages the cover sheet takes (Compose's page count before
// Preview): about 90 characters a line; about 20 lines fit on the cover
// sheet, about 40 on each continued page
export function coverPagesAbout(html) {
  const lines = toText(html).split('\n').reduce((n, l) => n + Math.max(1, Math.ceil(l.length / 90)), 0);
  return lines <= 20 ? 1 : 1 + Math.ceil((lines - 19) / 39);
}
